import { useRef, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { StarIcon as GiftIcon } from '@src/components/icons/StarIcon';
import { CogIcon } from '@src/components/icons/CogIcon';
import { WrenchIcon as UserExcludeIcon } from '@src/components/icons/WrenchIcon';

type SettingsTab = 'questionnaires' | 'appearance' | 'actions';

const icons = { appearance: GiftIcon, questionnaires: CogIcon, actions: UserExcludeIcon };

export function OrganizerSettingsTabs({ includeAppearance = true }: { includeAppearance?: boolean }) {
  const tabs: SettingsTab[] = includeAppearance ? ['questionnaires', 'appearance', 'actions'] : ['questionnaires', 'actions'];
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const active = tabs.find(tab => tab === (params.get('tab') === 'settings' ? params.get('settingsTab') : params.get('tab'))) ?? 'questionnaires';
  function select(tab: SettingsTab) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', 'settings');
      next.set('settingsTab', tab);
      return next;
    }, { preventScrollReset: true });
  }
  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault(); refs.current[next]?.focus(); select(tabs[next]);
  }
  return (
    <div className="organizer-people__tabs organizer-draw__tabs" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, ${includeAppearance ? '1fr' : '180px'}))` }} role="tablist" aria-label={t('organizerPanel.settings')}>
      {tabs.map((tab, index) => { const Icon = icons[tab]; return <button type="button" key={tab} role="tab"
        ref={node => { refs.current[index] = node; }}
        id={`settings-subtab-${tab}`} data-testid={`settings-subtab-${tab}`}
        aria-selected={active === tab} aria-controls={`settings-content-${tab}`} tabIndex={active === tab ? 0 : -1}
        onClick={() => select(tab)} onKeyDown={event => onKeyDown(event, index)}>
        <span className="organizer-people__tab-icon" aria-hidden="true"><Icon /></span>
        {t(`organizerPanel.${tab}`)}
      </button>; })}
    </div>
  );
}
